import React from 'react';
import SEOHead from '../components/SEOHead';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import SecurityIcon from '@mui/icons-material/Security';
import InfoIcon from '@mui/icons-material/Info';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import UpdateIcon from '@mui/icons-material/Update';
import CookieIcon from '@mui/icons-material/Cookie';
import ShareIcon from '@mui/icons-material/Share';

const MotionBox = motion(Box);
const MotionPaper = motion(Paper);

const PrivacyPolicy = () => {
  const theme = useTheme();
  const lastUpdated = "June 2025";

  const sections = [
    {
      title: "Information We Collect",
      icon: <InfoIcon />,
      content: [
        "Personal Information: Name, email address, phone number, and company details when you contact us or request our services.",
        "Technical Information: IP address, browser type, device information, and usage data when you visit our website.",
        "Project Information: Details about your project requirements, business needs, and technical specifications shared during consultations.",
        "Communication Records: Emails, messages, and call records related to our business relationship."
      ]
    },
    {
      title: "How We Use Your Information",
      icon: <SecurityIcon />,
      content: [
        "Provide Development Services: To deliver web development, mobile app development, and Shopify solutions.",
        "Communication: To respond to inquiries, provide project updates, and maintain client relationships.",
        "Service Improvement: To enhance our website functionality and service quality.",
        "Legal Compliance: To comply with applicable laws and regulations.",
        "Marketing: To send relevant updates about our services (with your consent)."
      ]
    },
    {
      title: "Information Sharing",
      icon: <ShareIcon />,
      content: [
        "We do not sell, trade, or rent your personal information to third parties.",
        "Service Providers: We may share information with trusted third-party services (hosting providers, payment processors, communication tools) necessary for service delivery.",
        "Legal Requirements: We may disclose information when required by law or to protect our rights and safety.",
        "Business Transfers: In case of merger, acquisition, or sale, your information may be transferred as part of business assets."
      ]
    },
    {
      title: "Data Security",
      icon: <SecurityIcon />,
      content: [
        "We implement industry-standard security measures to protect your information.",
        "SSL encryption for data transmission on our website.",
        "Secure servers and regular security updates for data storage.",
        "Access controls and authentication for our team members.",
        "Regular security audits and vulnerability assessments.",
        "However, no method of transmission over the internet is 100% secure."
      ]
    },
    {
      title: "Cookies and Tracking",
      icon: <CookieIcon />,
      content: [
        "Essential Cookies: Required for website functionality and security.",
        "Analytics Cookies: To understand website usage and improve user experience.",
        "Communication Cookies: For contact forms and live chat functionality.",
        "You can control cookie preferences through your browser settings.",
        "Disabling cookies may affect website functionality."
      ]
    },
    {
      title: "Your Rights",
      icon: <ContactMailIcon />,
      content: [
        "Access: Request information about the personal data we hold about you.",
        "Correction: Request correction of inaccurate or incomplete information.",
        "Deletion: Request deletion of your personal data (subject to legal requirements).",
        "Portability: Request a copy of your data in a structured format.",
        "Opt-out: Unsubscribe from marketing communications at any time.",
        "Contact us at info@devhopeslabs.nl to exercise these rights."
      ]
    },
    {
      title: "Third-Party Services",
      icon: <ShareIcon />,
      content: [
        "AWS/Cloud Hosting: For secure and reliable website hosting.",
        "Google Analytics: For website traffic analysis (anonymized data).",
        "Email Services: For communication and newsletter delivery.",
        "Payment Processors: Stripe, PayPal for secure payment processing.",
        "Communication Tools: For project management and client communication.",
        "These services have their own privacy policies that govern their use of your information."
      ]
    },
    {
      title: "International Data Transfers",
      icon: <UpdateIcon />,
      content: [
        "As a Netherlands-based company, we primarily process data within the EU.",
        "Some third-party services may process data outside the EU with appropriate safeguards.",
        "We ensure adequate protection levels for international data transfers.",
        "Data transfers comply with GDPR requirements and standard contractual clauses."
      ]
    },
    {
      title: "Data Retention",
      icon: <UpdateIcon />,
      content: [
        "Client project data: Retained for the duration of our business relationship plus 7 years for legal compliance.",
        "Website analytics: Retained for 26 months maximum.",
        "Marketing communications: Until you unsubscribe or request deletion.",
        "Legal documents: Retained as required by applicable laws.",
        "We regularly review and delete data that is no longer necessary."
      ]
    }
  ];

  return (
    <>
      <SEOHead
        title="Privacy Policy - DevHopes Labs Data Protection & Privacy"
        description="Privacy Policy for DevHopes Labs - Learn how we collect, use, and protect your personal information. Our commitment to GDPR compliance and data security for mobile and web development services."
        keywords="DevHopes Labs privacy policy, data protection, GDPR compliance, privacy rights, personal information protection, data security, mobile app development privacy, web development privacy, cookies policy"
        url="/privacy-policy"
        type="website"
        schemaType="WebPage"
        noindex={true}
      />

      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 10 }}>
        <Container maxWidth="lg">
          {/* Header */}
          <MotionBox
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            sx={{ textAlign: 'center', mb: 6 }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 800,
                background: 'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
              }}
            >
              Privacy Policy
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: 'auto', mb: 2 }}
            >
              Your privacy is important to us. This policy explains how DevHopes Labs collects, uses, and protects your personal information.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Last updated: {lastUpdated}
            </Typography>
          </MotionBox>

          {/* Introduction */}
          <MotionPaper
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            elevation={0}
            sx={{
              p: 4,
              mb: 4,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
              DevHopes Labs ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, use our services, or engage with us for web development, mobile app development, and Shopify solutions.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
              By using our services or website, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
            </Typography>
          </MotionPaper>

          {/* Sections */}
          {sections.map((section, index) => (
            <MotionPaper
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 * (index + 3) }}
              elevation={0}
              sx={{
                mb: 3,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  p: 3,
                  bgcolor: 'primary.50',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box sx={{ color: 'primary.main' }}>
                  {section.icon}
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {section.title}
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <List sx={{ p: 0 }}>
                  {section.content.map((item, itemIndex) => (
                    <ListItem key={itemIndex} sx={{ px: 0, py: 1 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={item}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontSize: '1rem',
                            lineHeight: 1.7,
                            color: 'text.primary',
                          },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </MotionPaper>
          ))}

          {/* Contact Information */}
          <MotionPaper
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            elevation={0}
            sx={{
              p: 4,
              mb: 6,
              borderRadius: 3,
              border: '2px solid',
              borderColor: 'primary.main',
              bgcolor: 'primary.50',
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
              Contact Us About Privacy
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
              If you have any questions about this Privacy Policy, your data rights, or our privacy practices, please contact us:
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Email:</strong> info@devhopeslabs.nl
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Company:</strong> DevHopes Labs
              </Typography>
              <Typography variant="body1">
                <strong>Location:</strong> Netherlands
              </Typography>
            </Box>
          </MotionPaper>

          {/* Updates Notice */}
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1 }}
            sx={{ textAlign: 'center', mb: 6 }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </Typography>
          </MotionBox>
        </Container>
      </Box>
    </>
  );
};

export default PrivacyPolicy; 